import Box from '@ahamove/web-components/lib/Box';
import cn from 'classnames';
import 'keen-slider/keen-slider.min.css';
import type { KeenSliderPlugin } from 'keen-slider/react';
import { useKeenSlider } from 'keen-slider/react';
import React, { useState } from 'react';
import type { BannerItem } from '@/api/cms/types';
import ButtonNavSwiper from '@/shared-components/ButtonNavSwiper';
import useIsMobile from '@/shared-hooks/useIsMobile';

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      slider.update();
    });
  });
  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

type Props = {
  homeBannerData: BannerItem[];
  shouldRefresh?: boolean;
};
export default function RentingBenefitSlider({ homeBannerData }: Props) {
  const [loaded, setLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: true,
      created() {
        setLoaded(true);
      },
      slideChanged(s) {
        setCurrentSlide(s.track.details.rel);
      },
      slides: {
        perView: 1,
        // spacing: isMobile ? 16 : 24,
        origin: 'center',
      },
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        const mouseOver = false;

        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 5000);
        }
        slider.on('created', () => {
          nextTimeout();
        });
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      },
      MutationPlugin,
    ]
  );

  return (
    <div className="group relative h-auto w-full overflow-hidden rounded-xl">
      <div className="navigation-wrapper">
        <div ref={sliderRef} className="keen-slider">
          {homeBannerData.map((e, index) => {
            return (
              <Box
                key={index}
                className="keen-slider__slide"
                sx={{
                  backgroundImage: `url(${e.desktop_image.data.attributes.url})`,
                  backgroundPosition: 'top center',
                  backgroundSize: 'cover',
                  aspectRatio: '1',
                }}
              />
            );
          })}
        </div>
      </div>

      {loaded && instanceRef.current && (
        <>
          <div className="absolute bottom-4 flex w-full items-center justify-center">
            {[
              ...Array(instanceRef.current.track.details.slides.length).keys(),
            ].map((idx) => {
              return (
                <button
                  key={idx}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx);
                  }}
                  className={
                    'dot ml-2 h-3 w-3 rounded-full ' +
                    (currentSlide === idx ? ' bg-white' : 'bg-[#A2A9B0]')
                  }
                ></button>
              );
            })}
          </div>
          {!isMobile ? (
            <div className="hidden group-hover:block">
              <ButtonNavSwiper
                left
                onClick={() => instanceRef.current?.prev()}
                loop={true}
                disabled={instanceRef?.current?.track.details.rel === 0}
                type="banner"
              />

              <ButtonNavSwiper
                onClick={() => instanceRef.current?.next()}
                loop={true}
                disabled={
                  instanceRef?.current?.track.details.rel ===
                  instanceRef.current.track.details.slides.length - 1
                }
                type="banner"
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
